import React from 'react';
import classnames from 'classnames';
import '../../css/table/table.less';

/**
 * @param {Array} items List of data items to be displayed
 * @param {Array} [columns] List of { propertyName: String; renderHeader?: Function; renderCell?: Function; }
 * @param {String} [className] Optional CSS class(es) to add to the table element
 * @param {Function} [keyGenerator] Function which generates unique id from each item, defaults to row index
 * @param {Function} [rowClassName] Passed each row data and row index and should return a CSS class string to add to the tr element
 * @param {Boolean} [isLoading] If true, a semi-opaque layer will be rendered over the table
 * @param {String} [wrapperClassName] Adds the given class to the div wrapper around the table
 * @param {String} [testId] Sets a data-testid attribute with the supplied value
 * @description Renders a table from a list of data objects
 */
const Table = ({
    items = [],
    columns = [],
    className = '',
    keyGenerator = (item, index) => index,
    rowClassName = () => {},
    isLoading = false,
    children,
    wrapperClassName = '',
    testId = '',
}) => {

    const itemList = items === null ? [] : items;

    const defaultRenderHeader = propertyName => propertyName;
    const defaultRenderCell = ({ item, propertyName }) => item[propertyName];

    const defaultColumns = Object.keys(itemList[0] || {}).map(propertyName => ({
        propertyName,
        renderHeader: defaultRenderHeader,
        renderCell: defaultRenderCell,
    }));

    const columnsToRender = columns.length === 0 ? defaultColumns : columns;

    const wrapperClasses = classnames({
        'table-wrapper': true,
        loading: isLoading,
        [wrapperClassName]: wrapperClassName.length > 0,
    });

    const showNoResultsMessage = isLoading === false && itemList.length === 0;

    return (
        <div className={wrapperClasses}>
            <table className={`${className}${itemList.length === 0 ? ' table-no-results' : ''}`} data-testid={testId}>
                <colgroup>
                    {
                        columnsToRender.map((column, index) => (
                            <col key={index} span="1" style={{ width: column.size || 'auto' }} />
                        ))
                    }
                </colgroup>
                <thead>
                    <tr>
                        {
                            columnsToRender.map((column, index) => (
                                <th key={index}>
                                    {
                                        column.renderHeader 
                                            ? column.renderHeader(column.propertyName) 
                                            : defaultRenderHeader(column.propertyName)
                                    }
                                </th>
                            ))
                        }
                    </tr>
                </thead>
                <tbody>
                    { !children ?
                        itemList.map((item, rowIndex) => (
                            <tr key={keyGenerator(item, rowIndex)} className={rowClassName(item, rowIndex)}>
                                {
                                    
                                    columnsToRender.map((column, cellIndex) => (
                                        <td key={cellIndex}>
                                            { 
                                                column.renderCell 
                                                    ? column.renderCell({ item, propertyName: column.propertyName, rowIndex, cellIndex }) 
                                                    : defaultRenderCell({ item, propertyName: column.propertyName, rowIndex, cellIndex }) 
                                            }
                                        </td>
                                    ))
                                }
                            </tr>
                        )) :
                        children
                    }
                </tbody>
            </table>
            {
                showNoResultsMessage ? <div className="table-no-results-message">No Results</div> : null
            }
        </div>
    );
};

export default Table;
